const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
const API_TIMEOUT = 10000; // 10 seconds timeout

// Custom error class that preserves the HTTP status code
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// Helper function to fetch with timeout
async function fetchWithTimeout(url: string, options: RequestInit = {}): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout - server is not responding');
    }
    throw error;
  }
}

export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  msg: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
}

export async function fetchEventCollection<T>(eventPath: string, fallbackPath: string): Promise<T[]> {
  try {
    const eventResponse = await api.get<T[]>(eventPath);
    if (eventResponse.success && eventResponse.data && eventResponse.data.length > 0) {
      return eventResponse.data;
    }
  } catch {
    // use shared catalog when the event has no linked rows yet
  }

  try {
    const fallback = await api.get<T[]>(fallbackPath);
    return fallback.success && fallback.data ? fallback.data : [];
  } catch {
    return [];
  }
}

export const api = {
  async post<T = any>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem("adminToken");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new ApiError(result.error || result.message || `HTTP error! status: ${response.status}`, response.status);
      }

      return result;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  },

  async get<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem("adminToken");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: "GET",
        headers,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new ApiError(result.error || result.message || `HTTP error! status: ${response.status}`, response.status);
      }

      return result;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  },

  async put<T = any>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem("adminToken");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new ApiError(result.error || result.message || `HTTP error! status: ${response.status}`, response.status);
      }

      return result;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  },

  async delete<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem("adminToken");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetchWithTimeout(`${API_BASE_URL}${endpoint}`, {
        method: "DELETE",
        headers,
        ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new ApiError(result.error || result.message || `HTTP error! status: ${response.status}`, response.status);
      }

      return result;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  },

  async contact(data: ContactFormData): Promise<ApiResponse> {
    return this.post("/api/contact", data);
  },

  async upload(file: File): Promise<ApiResponse<{ imageUrl: string }>> {
    try {
      const token = localStorage.getItem("adminToken");
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const formData = new FormData();
      formData.append("image", file);

      const response = await fetchWithTimeout(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers,
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new ApiError(result.error || result.message || `HTTP error! status: ${response.status}`, response.status);
      }

      return result;
    } catch (error) {
      console.error("Upload Error:", error);
      throw error;
    }
  },
};
